import {
  RESET_CURRENT_BUILDING,
  RESET_CURRENT_COMPLEX_LAYER,
  SET_CURRENT_BUILDING,
  SET_CURRENT_COMPLEX_LAYER,
  SET_CURRENT_MAP_DRAWING_TRANSPARENCY,
  SET_CURRENT_MAP_VECTOR_TILE_ID,
  SET_CURRENT_REAL_ESTATE,
  SET_CURRENT_REGION_LAYER,
  SET_FILTERED_MAP_LAYERS,
  SET_FILTERED_MAP_POI_LAYERS,
  SET_IS_SATELLITE_ACTIVE,
  SET_OUTDOOR_POI_DATA_FOR_REAL_ESTATE,
  SET_OUTDOOR_ZONE_DATA_FOR_REAL_ESTATE,
  SET_POI_CATEGORIES_FOR_SELECTED_REAL_ESTATE,
  SET_ZONE_CATEGORIES_FOR_REAL_ESTATE,
  SET_HOVERED_COMPLEX_ID,
  SET_MAP_VECTOR_TILE_STATE,
  SET_POI_CATEGORIES_FOR_SELECTED_BUILDING,
  SET_SHOW_ZONE_MENU,
  SET_POI_MENU
} from '../mutation-types'
import {
  GET_OUTDOOR_POI_DATA_FOR_REAL_ESTATE,
  GET_OUTDOOR_ZONE_DATA_FOR_REAL_ESTATE,
  GET_POI_CATEGORIES_FOR_SELECTED_BUILDING,
  RESET_CURRENT_REAL_ESTATE
} from '@/store/action-types'
import { getLineCategories } from '@/services/lines.service'
import { getZoneCategories, getZoneTypeFeaturesV2 } from '@/services/zones.service'
import {
  getCategoriesForBuilding,
  getPoiCategoriesForRealEstate,
  getPoisForSelectedRealEstatePerCategory
} from '@/services/pois.service'
import { getPoiWithAddedProperties } from '@/utils/data'
import { useGeneralStore as piniaStore } from '@/pinia'

export default {
  namespaced: true,
  state: {
    currentDrawingTransparency: 0.8,
    currentMapVectorTileId: null,
    dataForComplex: null,
    filteredLayers: [
      {
        disabled: false,
        id: 'building',
        title: 'general__data__filter-building',
        types: [{}],
        visible: true
      },
      {
        disabled: false,
        id: 'realestates',
        title: 'general__data__filter-realestates',
        types: [{}],
        visible: true
      }
    ],
    filteredPoiLayers: [],
    mapVectorTileState: 'hidden',
    isSatelliteActive: false,
    selectedBuilding: {},
    selectedComplex: null,
    selectedRealEstate: null,
    selectedRegion: 0, // Currently, region represents realm overview (nothing selected)
    selectedRegionName: 'Alla',
    hoveredComplexId: '',
    outdoorPoisForSelectedRealEstate: [],
    outdoorZonesForSelectedRealEstate: [],
    poiCategoriesForSelectedBuilding: [],
    poiCategoriesForSelectedRealEstate: [],
    zoneCategoriesForSelectedRealEstate: []
  },
  getters: {
    selectedBuildingId: (state) => {
      return state.selectedBuilding.id ? state.selectedBuilding.id : ''
    },
    selectedBuildingName: (state) => {
      return state.selectedBuilding.name ? state.selectedBuilding.name : ''
    },
    selectedComplexId: (state) => {
      return state.selectedComplex != null ? state.selectedComplex.belongsToId : ''
    },
    selectedComplexName: (state) => {
      return state.selectedComplex != null ? state.selectedComplex.title : ''
    },
    selectedRealEstateId: (state) => {
      return state.selectedRealEstate != null ? state.selectedRealEstate.id : ''
    },
    selectedRealEstateName: (state) => {
      return state.selectedRealEstate != null ? state.selectedRealEstate.name : ''
    }
  },
  mutations: {
    [SET_HOVERED_COMPLEX_ID](state, payload) {
      state.hoveredComplexId = payload
    },
    [RESET_CURRENT_BUILDING](state) {
      state.selectedBuilding = {}
    },
    [RESET_CURRENT_COMPLEX_LAYER](state) {
      state.selectedComplex = null
    },
    [SET_CURRENT_BUILDING](state, payload) {
      state.selectedBuilding = payload.building
    },
    // Takes a payload with complex: String
    [SET_CURRENT_COMPLEX_LAYER](state, payload) {
      state.selectedComplex = payload.complex
    },
    [SET_CURRENT_MAP_DRAWING_TRANSPARENCY](state, payload) {
      state.currentDrawingTransparency = payload.currentDrawingTransparency
    },
    [SET_CURRENT_MAP_VECTOR_TILE_ID](state, payload) {
      state.currentMapVectorTileId = payload
    },
    // Takes a payload with real estate id: String
    [SET_CURRENT_REAL_ESTATE](state, payload) {
      state.selectedRealEstate = payload.realEstate
    },
    // Takes a payload with regionNumber: Number and regionName: String
    [SET_CURRENT_REGION_LAYER](state, payload) {
      state.selectedRegion = payload.regionNumber
      state.selectedRegionName = payload.regionName
    },
    [SET_FILTERED_MAP_LAYERS](state, payload) {
      state.filteredLayers = payload.filteredLayers
    },
    [SET_FILTERED_MAP_POI_LAYERS](state, payload) {
      state.filteredPoiLayers = payload.filteredPoiLayers
    },
    [SET_IS_SATELLITE_ACTIVE](state, payload) {
      state.isSatelliteActive = payload.value
    },
    [SET_OUTDOOR_POI_DATA_FOR_REAL_ESTATE](state, payload) {
      state.outdoorPoisForSelectedRealEstate = payload.pois
    },
    // Takes a payload with outdoor data from BE
    [SET_OUTDOOR_ZONE_DATA_FOR_REAL_ESTATE](state, payload) {
      state.outdoorZonesForSelectedRealEstate = payload.zones
    },
    [SET_POI_CATEGORIES_FOR_SELECTED_REAL_ESTATE](state, payload) {
      state.poiCategoriesForSelectedRealEstate = payload.categories
    },
    [SET_ZONE_CATEGORIES_FOR_REAL_ESTATE](state, payload) {
      state.zoneCategoriesForSelectedRealEstate = payload.categories
    },
    [SET_MAP_VECTOR_TILE_STATE](state, payload) {
      if (['hidden', 'loading', 'visible'].includes(payload)) state.mapVectorTileState = payload
    },
    [SET_POI_CATEGORIES_FOR_SELECTED_BUILDING](state, payload) {
      state.poiCategoriesForSelectedBuilding = payload
    }
  },
  actions: {
    async [GET_OUTDOOR_POI_DATA_FOR_REAL_ESTATE]({ commit, state }, payload) {
      if (!payload.categoryId || !payload.realEstateId || !payload.typeId) return
      const filteredPoiLayers = [...state.filteredPoiLayers]
      const isCategoryDataAlreadyFetched = state.outdoorPoisForSelectedRealEstate.some(
        (feature) => feature.properties.typeId === payload.typeId
      )
      if (!isCategoryDataAlreadyFetched) {
        let poiFeaturesForType = await getPoisForSelectedRealEstatePerCategory(
          payload.categoryId,
          payload.realEstateId,
          payload.typeId
        )
        const updatedPois = []
        if (poiFeaturesForType)
          poiFeaturesForType.forEach((poi) =>
            updatedPois.push(
              getPoiWithAddedProperties(poi, state.poiCategoriesForSelectedRealEstate)
            )
          )
        poiFeaturesForType = updatedPois
        if (poiFeaturesForType?.length) {
          const existingPoisArray = state.outdoorPoisForSelectedRealEstate
          const poisArray = existingPoisArray.concat(poiFeaturesForType)
          commit('SET_OUTDOOR_POI_DATA_FOR_REAL_ESTATE', { pois: poisArray })
          const poiCategory = state.filteredPoiLayers.find(
            (category) => category.id === payload.categoryId
          )
          const poiTypeInfo = poiCategory.types.find((type) => type.id === payload.typeId)
          poiTypeInfo.visible = payload.checked
          poiCategory.visible =
            poiCategory.types.filter((x) => x.visible === true).length === poiCategory.types.length
        }
      }
      if (isCategoryDataAlreadyFetched) {
        filteredPoiLayers.forEach((layer) => {
          layer.types.forEach((type) => {
            if (type.id === payload.typeId) {
              type.visible = payload.checked
              layer.visible =
                layer.types.filter((x) => x.visible === true).length === layer.types.length
            }
          })
        })
      }
      commit('SET_FILTERED_MAP_POI_LAYERS', { filteredPoiLayers })
    },
    async [GET_OUTDOOR_ZONE_DATA_FOR_REAL_ESTATE]({ commit, state }, payload) {
      const filteredLayers = [...state.filteredLayers]
      const isTypeDataAlreadyFetched = state.outdoorZonesForSelectedRealEstate.some(
        (feature) => feature.properties.typeId === payload.typeId
      )
      if (!isTypeDataAlreadyFetched) {
        const zoneFeaturesForType = await getZoneTypeFeaturesV2(
          payload.realEstateId,
          payload.categoryId,
          payload.typeId
        )
        if (!zoneFeaturesForType) return
        const selectedType = zoneFeaturesForType.filter(
          (type) => type.properties.typeId === payload.typeId
        )
        const existingZonesArray = state.outdoorZonesForSelectedRealEstate
        const zonesArray = existingZonesArray.concat(selectedType)
        commit('SET_OUTDOOR_ZONE_DATA_FOR_REAL_ESTATE', { zones: zonesArray })
        const zoneCategory = state.filteredLayers.find(
          (category) => category.id === payload.categoryId
        )
        const zoneTypeInfo = zoneCategory.types.find((type) => type.id === payload.typeId)
        zoneTypeInfo.visible = payload.checked
        zoneCategory.visible =
          zoneCategory.types.filter((x) => x.visible === true).length === zoneCategory.types.length
      }

      if (isTypeDataAlreadyFetched) {
        filteredLayers.forEach((layer) => {
          layer.types.forEach((type) => {
            if (type.id === payload.typeId) {
              type.visible = payload.checked
              layer.visible =
                layer.types.filter((x) => x.visible === true).length === layer.types.length
            }
          })
        })
      }
      commit('SET_FILTERED_MAP_LAYERS', { filteredLayers })
    },
    async [GET_POI_CATEGORIES_FOR_SELECTED_BUILDING]({ commit }, payload) {
      try {
        const poiCategories = await getCategoriesForBuilding(payload)
        commit(SET_POI_CATEGORIES_FOR_SELECTED_BUILDING, poiCategories)
      } catch (e) {
        console.error(e)
      }
    },
    async onRealEstateChange({ commit, state, rootGetters }, realEstateId) {
      commit(SET_POI_MENU, false, { root: true })
      commit(SET_SHOW_ZONE_MENU, false, { root: true })
      piniaStore().setShowLineMenu(false)
      commit('SET_OUTDOOR_ZONE_DATA_FOR_REAL_ESTATE', { zones: [] })
      commit('SET_OUTDOOR_POI_DATA_FOR_REAL_ESTATE', { pois: [] })
      piniaStore().setLineDataForCoreEntity([])
      if (rootGetters.hasZoneAccess) {
        let zoneCategoriesForSelectedRealEstate = []
        try {
          zoneCategoriesForSelectedRealEstate = await getZoneCategories(realEstateId)
        } catch (e) {
          console.error(e)
        }
        if (zoneCategoriesForSelectedRealEstate.data.content.length) {
          commit('SET_ZONE_CATEGORIES_FOR_REAL_ESTATE', {
            categories: zoneCategoriesForSelectedRealEstate.data.content
          })
          const filteredLayers = state.filteredLayers
          zoneCategoriesForSelectedRealEstate.data.content.forEach((category) => {
            category.types.forEach((type) => {
              type.visible = false
              type.disabled = false
            })
            if (!filteredLayers.find((layer) => layer.id === category.id))
              filteredLayers.push({
                disabled: false,
                id: category.id,
                name: category.name,
                types: category.types,
                visible: false
              })
          })
          commit('SET_FILTERED_MAP_LAYERS', { filteredLayers })
        }
      }
      // Change to access management for line
      if (rootGetters.hasLineReaderAccess) {
        const filteredLineLayers = []
        let lineCategoriesForSelectedRealEstate = []
        try {
          lineCategoriesForSelectedRealEstate = await getLineCategories(realEstateId)
        } catch (e) {
          console.error(e)
        }
        if (lineCategoriesForSelectedRealEstate?.data?.content?.length) {
          piniaStore().setLineCategoriesForRealEstate(
            lineCategoriesForSelectedRealEstate.data.content
          )
          lineCategoriesForSelectedRealEstate.data.content.forEach((category) => {
            category.types.forEach((type) => {
              type.visible = false
              type.disabled = false
            })
            filteredLineLayers.push({
              disabled: false,
              id: category.id,
              name: category.name,
              types: category.types,
              visible: false
            })
          })
        }
        piniaStore().setFilteredLines(filteredLineLayers)
      }
      if (rootGetters.hasPoiReaderAccess) {
        let poiCategoriesForSelectedRealEstate = []
        try {
          poiCategoriesForSelectedRealEstate = await getPoiCategoriesForRealEstate(
            realEstateId,
            true
          )
        } catch (error) {
          console.error(error)
        }
        if (poiCategoriesForSelectedRealEstate?.length) {
          commit('SET_POI_CATEGORIES_FOR_SELECTED_REAL_ESTATE', {
            categories: poiCategoriesForSelectedRealEstate
          })
          const filteredPoiLayers = state.filteredPoiLayers
          poiCategoriesForSelectedRealEstate.forEach((category) => {
            category.types.forEach((type) => {
              type.visible = false
              type.disabled = false
            })
            if (!filteredPoiLayers.find((layer) => layer.id === category.id))
              filteredPoiLayers.push({
                disabled: false,
                id: category.id,
                name: category.name,
                visible: false,
                types: category.types
              })
          })
          commit('SET_FILTERED_MAP_POI_LAYERS', { filteredPoiLayers })
        }
      }
    },
    [RESET_CURRENT_REAL_ESTATE]({ commit, dispatch, state }) {
      commit('SET_CURRENT_REAL_ESTATE', { realEstate: null })
      dispatch('iv/RESET_IVION_INSTANCES', {}, { root: true })
      let filteredLayers = state.filteredLayers.filter(
        (layer) => layer.id === 'building' || layer.id === 'realestates'
      )
      piniaStore().setFilteredDrawings([])
      piniaStore().setFilteredLines([])
      piniaStore().setFilteredOrthophotos([])
      piniaStore().setLineDataForCoreEntity([])
      piniaStore().setLineCategoriesForRealEstate([])
      commit('SET_FILTERED_MAP_LAYERS', { filteredLayers })
      commit('SET_FILTERED_MAP_POI_LAYERS', { filteredPoiLayers: [] })
      commit('SET_OUTDOOR_ZONE_DATA_FOR_REAL_ESTATE', { zones: [] })
      commit('SET_OUTDOOR_POI_DATA_FOR_REAL_ESTATE', { pois: [] })
      commit('SET_POI_CATEGORIES_FOR_SELECTED_REAL_ESTATE', { categories: [] })
      commit('SET_ZONE_CATEGORIES_FOR_REAL_ESTATE', { categories: [] })
    },
    async ['COMPLEX_CLICKED']({ commit, dispatch }, payload) {
      const { complexProperties } = payload
      commit('SET_CLICKED_ITEM_TYPE', { type: 'complex' }, { root: true })
      commit('RESET_CURRENT_BUILDING')
      commit('RESET_CURRENT_ROOM', null, { root: true })
      dispatch('RESET_CURRENT_REAL_ESTATE')
      dispatch('RESET_CURRENT_LEVEL', null, { root: true })
      dispatch('CHANGE_COMPLEX', { complex: complexProperties }, { root: true })
    }
  }
}
